import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Stack,
  ButtonBase,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  BarChartOutlined as BarChartOutlinedIcon,
  PhotoLibraryOutlined as PhotoLibraryOutlinedIcon,
  HandshakeOutlined as HandshakeOutlinedIcon,
  HandymanOutlined as HandymanOutlinedIcon,
  MailOutlineOutlined as MailOutlineOutlinedIcon,
  LogoutOutlined as LogoutOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
} from '@mui/icons-material';
import { navigate } from '../lib/navigation';
import Button from '../components/Button';
import FloatingHelpButton from '../components/FloatingHelpButton';
import LogoMark from '../components/LogoMark';
import PublishProfileDialog from '../components/PublishProfileDialog';
import { useProfileCompletion } from '../lib/useProfileCompletion';

const drawerWidth = 240;

function getPageTitle(path) {
  if (path === '/dashboard' || path === '/dashboard/profile' || path === '/dashboard/settings') return 'Profile editor';
  if (path === '/dashboard/socials') return 'Social stats';
  if (path === '/dashboard/portfolio') return 'Content samples';
  if (path === '/dashboard/portfolio/new') return 'Upload content sample';
  if (path.startsWith('/dashboard/portfolio/edit/')) return 'Edit content sample';
  if (path === '/dashboard/collaborations') return 'Brand history';
  if (path === '/dashboard/collaborations/new') return 'Add brand work';
  if (path.startsWith('/dashboard/collaborations/')) return 'Brand history';
  if (path === '/dashboard/tools') return 'Creator tools';
  if (path === '/dashboard/opportunities') return 'Brand Opportunities';
  return 'Dashboard';
}

function DashboardLayout({ identity, auth, authActions, identityActions, children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const nav = [
    ['Profile', '/dashboard/profile', <PersonIcon />, 'profile'],
    ['Social stats', '/dashboard/socials', <BarChartOutlinedIcon />, 'socials'],
    ['Content samples', '/dashboard/portfolio', <PhotoLibraryOutlinedIcon />, 'portfolio'],
    ['Brand history', '/dashboard/collaborations', <HandshakeOutlinedIcon />, 'collaborations'],
    ['Brand Opportunities', '/dashboard/opportunities', <AutoAwesomeOutlinedIcon />],
    ['Creator tools', '/dashboard/tools', <HandymanOutlinedIcon />],
    ['Contact us', '/contact', <MailOutlineOutlinedIcon />],
  ];
  const profileNav = nav.slice(0, 4);
  const path = window.location.pathname;
  const isActive = (href) => path === href || (path === '/dashboard' && href === '/dashboard/profile');
  const isProfileNavActive = (href) => {
    if (href === '/dashboard/profile') return path === '/dashboard' || path === href;
    return path === href || path.startsWith(`${href}/`);
  };
  const previewUrl = '/dashboard/preview';
  const { sections } = useProfileCompletion(identity.creator);
  const sectionStatus = Object.fromEntries(sections.map((section) => [section.key, section.status]));
  const profileStatuses = sections.filter((section) => ['profileStatus', 'profileBasics', 'profileImage', 'location'].includes(section.key));
  sectionStatus.profile = profileStatuses.every((section) => section.status === 'Complete')
    ? 'Complete'
    : profileStatuses.some((section) => section.status !== 'Not started')
      ? 'Needs attention'
      : 'Not started';
  const pageTitle = getPageTitle(path);
  const logout = () => {
    authActions.logout();
    navigate('/');
  };
  const openPreview = () => {
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };
  const togglePublished = () => {
    if (identity.creator.published) {
      identityActions.updateCreator({ published: false });
    } else {
      setPublishOpen(true);
    }
  };

  const drawerContent = (
    <Box sx={{ py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, pb: 2 }}>
        <LogoMark />
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {nav.map(([label, href, icon, sectionKey]) => (
          <ListItemButton
            key={href}
            selected={isActive(href)}
            onClick={() => {
              if (href === '/contact') {
                window.open(href, '_blank', 'noopener,noreferrer');
              } else {
                navigate(href);
              }
              setMobileOpen(false);
            }}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{label}</span>
                  {sectionKey && sectionStatus[sectionKey] !== 'Complete' && (
                    <Box
                      aria-label={`${label}: ${sectionStatus[sectionKey] || 'Not started'}`}
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: sectionStatus[sectionKey] === 'Needs attention' ? 'warning.main' : 'action.disabled',
                      }}
                    />
                  )}
                </Stack>
              }
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ mx: 2, mb: 1 }} />
      <List>
        <ListItemButton onClick={logout}>
          <ListItemIcon>
            <LogoutOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Log out" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: '#1c1530',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ display: { md: 'none' }, color: 'white' }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              display="block"
              sx={{ lineHeight: 1.4, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}
            >
              Dashboard
            </Typography>
            <Typography
              variant="h6"
              fontWeight={800}
              noWrap
              sx={{ lineHeight: 1.2, color: 'white', fontSize: { xs: '1.05rem', sm: '1.25rem' }, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {pageTitle}
            </Typography>
          </Box>
          <Tooltip title="Preview your profile, including unfinished sections">
            <span>
              <Button
                onClick={openPreview}
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
              >
                Preview public profile
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={identity.creator.published ? 'Take your public profile offline' : 'Check whether your profile is ready'}>
            <span>
              <Button
                variant="light"
                onClick={togglePublished}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  whiteSpace: 'nowrap',
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.35)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                {identity.creator.published ? 'Unpublish' : 'Publish profile'}
              </Button>
            </span>
          </Tooltip>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              px: 0,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: identity.creator.published ? '#7FE58A' : 'rgba(255,255,255,0.4)',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                color: identity.creator.published ? '#7FE58A' : 'rgba(255,255,255,0.6)',
              }}
            >
              {identity.creator.published ? 'Published' : 'Draft'}
            </Typography>
          </Box>
          <IconButton
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            aria-label="Account menu"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 2 }}
          >
            <Avatar
              src={identity.creator.avatarUrl}
              alt={`${identity.creator.displayName || 'Creator'} avatar`}
              sx={{ border: '2px solid', borderColor: 'primary.main' }}
            />
            <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.6)' }} />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {identity.creator.displayName || 'Untitled profile'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {auth?.user?.email}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mt: 0.75 }}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: identity.creator.published ? 'success.main' : 'action.disabled',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    color: identity.creator.published ? 'success.main' : 'text.secondary',
                  }}
                >
                  {identity.creator.published ? 'Published' : 'Draft'}
                </Typography>
              </Stack>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                navigate('/dashboard/profile');
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Edit profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                openPreview();
              }}
            >
              <ListItemIcon>
                <VisibilityOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Preview public profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                togglePublished();
              }}
            >
              <ListItemIcon>
                <CloudUploadOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {identity.creator.published ? 'Unpublish' : 'Publish profile'}
            </MenuItem>
            <Divider />
            <MenuItem onClick={logout}>
              <ListItemIcon>
                <LogoutOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
        }}
        open
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}
      >
        {drawerContent}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` }, minWidth: 0 }}>
        <Toolbar />
        <Box
          component="nav"
          aria-label="Creator profile pages"
          sx={{
            display: { xs: mobileOpen ? 'none' : 'block', md: 'none' },
            position: 'sticky',
            top: { xs: 56, sm: 64 },
            zIndex: 10,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Stack direction="row" sx={{ minWidth: 'max-content', px: { xs: 1, sm: 2 } }}>
            {profileNav.map(([label, href, icon]) => {
              const active = isProfileNavActive(href);
              return (
                <ButtonBase
                  key={href}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => navigate(href)}
                  sx={{
                    position: 'relative',
                    minHeight: 52,
                    px: { xs: 1.5, sm: 2 },
                    gap: 0.75,
                    color: active ? 'primary.main' : 'text.secondary',
                    fontWeight: active ? 700 : 500,
                    whiteSpace: 'nowrap',
                    '&::after': {
                      content: active ? '""' : 'none',
                      position: 'absolute',
                      left: 12,
                      right: 12,
                      bottom: 0,
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                      bgcolor: 'primary.main',
                    },
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {icon}
                  <Typography component="span" variant="body2" sx={{ fontWeight: 'inherit' }}>
                    {label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Stack>
        </Box>
        {children}
      </Box>
      <PublishProfileDialog
        open={publishOpen}
        creator={identity.creator}
        onClose={() => setPublishOpen(false)}
        onPublish={() => {
          setPublishOpen(false);
          identityActions.updateCreator({ published: true });
        }}
      />
      <FloatingHelpButton />
    </Box>
  );
}

export default DashboardLayout;
